import * as React from "react";
import { MagnifyingGlass, X, CaretDown, Check, Plus } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

const MAX_RESULTS = 50;

/**
 * Confidential searchable select.
 *
 * Unlike a normal dropdown, this NEVER lists all options when opened.
 * Matching options appear only after the user types `minChars` (default 2)
 * or more characters, so the full dataset is not exposed by browsing.
 *
 * When `allowCreate` is true and there is no exact match, offers
 * "Use as new: {typed}" so users can enter names not yet in the master list.
 */
const SearchSelect = React.forwardRef(
  (
    {
      value,
      onValueChange,
      onCreateOption,
      options = [],
      placeholder = "Type to search...",
      minChars,
      disabled = false,
      allowClear = true,
      allowCreate = false,
      createLabel = "Use as new",
      className,
      id,
      "data-testid": dataTestId,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const [highlighted, setHighlighted] = React.useState(-1);
    const containerRef = React.useRef(null);
    const inputRef = React.useRef(null);

    const trimmed = query.trim();
    // Small lists (≤40): browse on open. Large lists: type 2+ chars (confidential).
    const effectiveMinChars =
      typeof minChars === "number"
        ? minChars
        : options.length > 0 && options.length <= 40
          ? 0
          : 2;
    const canSearch = trimmed.length >= effectiveMinChars;

    const matches = React.useMemo(() => {
      if (!canSearch) return [];
      if (!trimmed) return options.slice(0, MAX_RESULTS);
      const q = trimmed.toLowerCase();
      return options.filter((o) => String(o).toLowerCase().includes(q)).slice(0, MAX_RESULTS);
    }, [options, trimmed, canSearch]);

    const exactMatch = React.useMemo(
      () => options.some((o) => String(o).toLowerCase() === trimmed.toLowerCase()),
      [options, trimmed]
    );
    const showCreate = allowCreate && trimmed.length >= Math.max(effectiveMinChars, 2) && trimmed && !exactMatch;

    React.useEffect(() => {
      const onClickOutside = (e) => {
        if (containerRef.current && !containerRef.current.contains(e.target)) {
          setOpen(false);
          setQuery("");
        }
      };
      document.addEventListener("mousedown", onClickOutside);
      return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    const selectOption = (option) => {
      onValueChange?.(option);
      setOpen(false);
      setQuery("");
    };

    const createOption = () => {
      const name = trimmed;
      if (!name) return;
      if (onCreateOption) onCreateOption(name);
      else onValueChange?.(name);
      setOpen(false);
      setQuery("");
    };

    const clearSelection = (e) => {
      e.stopPropagation();
      onValueChange?.("");
      setQuery("");
      setOpen(true);
      requestAnimationFrame(() => inputRef.current?.focus());
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setQuery("");
        return;
      }
      const total = matches.length + (showCreate ? 1 : 0);
      if (!total) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlighted((h) => Math.min(h + 1, total - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlighted((h) => Math.max(h - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (showCreate && highlighted === matches.length) createOption();
        else if (highlighted >= 0 && highlighted < matches.length) selectOption(matches[highlighted]);
        else if (showCreate && matches.length === 0) createOption();
      }
    };

    React.useEffect(() => {
      if (matches.length) setHighlighted(0);
      else if (showCreate) setHighlighted(0);
      else setHighlighted(-1);
    }, [trimmed, matches.length, showCreate]);

    return (
      <div ref={containerRef} className={cn("relative", className)}>
        {!open ? (
          <button
            type="button"
            id={id}
            ref={ref}
            disabled={disabled}
            data-testid={dataTestId}
            onClick={() => {
              setOpen(true);
              requestAnimationFrame(() => inputRef.current?.focus());
            }}
            className={cn(
              "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
              !value && "text-muted-foreground"
            )}
          >
            <span className="truncate">{value || placeholder}</span>
            <span className="flex items-center gap-1 shrink-0">
              {allowClear && value && !disabled && (
                <X
                  size={14}
                  className="opacity-50 hover:opacity-100"
                  onClick={clearSelection}
                  aria-label="Clear selection"
                />
              )}
              <CaretDown size={14} className="opacity-50" />
            </span>
          </button>
        ) : (
          <div className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-white px-3 shadow-sm ring-1 ring-ring">
            <MagnifyingGlass size={14} className="text-slate-400 shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                effectiveMinChars === 0
                  ? "Type to filter..."
                  : `Type ${effectiveMinChars}+ letters to search...`
              }
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              data-testid={dataTestId ? `${dataTestId}-input` : undefined}
            />
          </div>
        )}

        {open && (
          <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-60 overflow-y-auto">
            {!canSearch ? (
              <p className="px-3 py-3 text-xs text-slate-400">
                Type at least {effectiveMinChars} character{effectiveMinChars === 1 ? "" : "s"} to see matching results.
                {allowCreate ? " You can also add a new name if it is not in the list." : ""}
              </p>
            ) : (
              <>
                {matches.length === 0 && !showCreate && (
                  <p className="px-3 py-3 text-xs text-slate-400">No matches found for "{trimmed}".</p>
                )}
                {matches.map((option, idx) => (
                  <button
                    key={option}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectOption(option)}
                    onMouseEnter={() => setHighlighted(idx)}
                    className={cn(
                      "flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50",
                      idx === highlighted && "bg-slate-100",
                      option === value && "font-medium"
                    )}
                  >
                    <span className="truncate">{option}</span>
                    {option === value && <Check size={14} className="text-emerald-600 shrink-0" />}
                  </button>
                ))}
                {showCreate && (
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={createOption}
                    onMouseEnter={() => setHighlighted(matches.length)}
                    className={cn(
                      "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-blue-700 hover:bg-blue-50 border-t border-slate-100",
                      highlighted === matches.length && "bg-blue-50"
                    )}
                    data-testid={dataTestId ? `${dataTestId}-create` : undefined}
                  >
                    <Plus size={14} className="shrink-0" />
                    <span className="truncate">{createLabel}: <strong>{trimmed}</strong></span>
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  }
);
SearchSelect.displayName = "SearchSelect";

export { SearchSelect };
